/**
 * Dietician Registration Flow Hook
 *
 * This module provides the main state management hook for the dietician
 * registration workflow. Implements step determination based on API calls
 * and handles step submission with API integration.
 *
 * @module hooks/useDieticianRegistrationFlow
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import type {
  DieticianRegistrationState,
  DieticianRegistrationData,
  DieticianRegistrationApiRequest,
  DieticianProfileApiRequest
} from '@/types/dieticianRegistration';
import type { HealthCondition } from '@/types/referral';
import { LanguageCode } from '@/types/languageCodes';
import { useDieticianValidation } from './useDieticianValidation';
import { useAuth } from '@/contexts/AuthContext';
// @deprecated - Migrated to use unified ApiService
// import { DieticianRegistrationApiService } from '@/services/dieticianRegistrationApi';
// import ProviderRegistrationApiService from '@/services/providerRegistrationApi';
import { apiService } from '@/services/ApiService';
import { AuthServiceFactory } from '@/services/auth/AuthServiceFactory';
import { BaseApiError } from '@/services/shared';
import type { DieticianScheduleFormData } from './useDieticianScheduleForm';
import { FileUtils } from '@/utils/fileUtils';
import { feeFieldsToPolicy, type FeePolicyFields } from '@/utils/feePolicy';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * Form data types for each step
 */
export interface DieticianRegistrationFormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface DieticianProfileFormData {
  firstName: string;
  lastName: string;
  areasOfPractice: HealthCondition[];
  languagesSpoken: LanguageCode[];
  phone: {
    countryCode: string;
    isoCode: string;
    number: string;
  };
  apdNumber: string;
  practiceAddress: {
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  photo?: File;
  /** "Fees and payment" section (design-patient-payment-guidance §4.13.4) */
  feePolicy: FeePolicyFields;
}

/**
 * Hook return type
 */
export interface UseDieticianRegistrationFlowReturn {
  state: DieticianRegistrationState;
  actions: {
    setCurrentStep: (step: number) => void;
    updateFormData: (data: Partial<DieticianRegistrationData>) => void;
    submitRegistration: (formData: DieticianRegistrationFormData) => Promise<void>;
    submitProfile: (formData: DieticianProfileFormData) => Promise<void>;
    submitSchedule: (formData: DieticianScheduleFormData) => Promise<void>;
    resetFlow: () => void;
    setError: (field: string, message: string) => void;
    clearErrors: () => void;
    setLoading: (loading: boolean) => void;
  };
  validation: {
    validateStep: (step: number) => boolean;
    getStepErrors: (step: number) => string[];
    canProceedToStep: (step: number) => boolean;
    isStepCompleted: (step: number) => boolean;
    isFormValid: boolean;
  };
  computed: {
    progressPercentage: number;
  };
}

/**
 * Main dietician registration flow hook
 */
export const useDieticianRegistrationFlow = (): UseDieticianRegistrationFlowReturn => {
  const logger = getLogger();
  const { signIn } = useAuth();

  // State management
  const [registrationState, setRegistrationState] = useState<DieticianRegistrationState>({
    currentStep: 1,
    formData: {},
    isLoading: false,
    errors: {},
    isSubmitting: false,
    completedSteps: new Set()
  });

  // Validation hook
  const validation = useDieticianValidation();

  // Actions
  const setCurrentStep = useCallback((step: number): void => {
    if (step >= 1 && step <= 4) {
      setRegistrationState(prevState => ({
        ...prevState,
        currentStep: step as 1 | 2 | 3 | 4,
        errors: {} // Clear errors when changing steps
      }));
    }
  }, []);

  const updateFormData = useCallback((data: Partial<DieticianRegistrationData>): void => {
    setRegistrationState(prevState => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        ...data
      }
    }));
  }, []);

  const setError = useCallback((field: string, message: string): void => {
    setRegistrationState(prevState => ({
      ...prevState,
      errors: {
        ...prevState.errors,
        [field]: message
      }
    }));
  }, []);

  const clearErrors = useCallback((): void => {
    setRegistrationState(prevState => ({
      ...prevState,
      errors: {}
    }));
  }, []);

  const setLoading = useCallback((loading: boolean): void => {
    setRegistrationState(prevState => ({
      ...prevState,
      isLoading: loading
    }));
  }, []);

  const setSubmitting = useCallback((submitting: boolean): void => {
    setRegistrationState(prevState => ({
      ...prevState,
      isSubmitting: submitting
    }));
  }, []);

  // Step 1: Registration submission
  const submitRegistration = useCallback(async (formData: DieticianRegistrationFormData): Promise<void> => {
    setSubmitting(true);
    clearErrors();

    try {
      // Validate form data
      const validationResult = validation.validateStep(1, formData);
      if (!validationResult.isValid) {
        setError('form', validationResult.error || 'Validation failed');
        return;
      }

      // Submit to API
      const apiRequest: DieticianRegistrationApiRequest = {
        email: formData.email.trim(),
        password: formData.password,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        role: 'dietician'
      };

      // Make actual API call using unified ApiService
      const result = await apiService.dietician.registerDietician(apiRequest);

      // Log in after successful registration
      await signIn({
        email: apiRequest.email,
        password: apiRequest.password
      });

      // Update state with success
      updateFormData({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName
      });

      setRegistrationState(prevState => ({
        ...prevState,
        registrationId: (result.data as { userId?: string; user_id?: string })?.userId ||
                       (result.data as { userId?: string; user_id?: string })?.user_id ||
                       'temp-id',
        completedSteps: new Set([...prevState.completedSteps, 1])
      }));

      // Proceed to next step
      setCurrentStep(2);

    } catch (error) {
      if (error instanceof BaseApiError && error.fieldErrors) {
        Object.entries(error.fieldErrors).forEach(([field, message]) => {
          setError(field, message);
        });
      } else {
        setError('form', error instanceof Error ? error.message : 'Registration failed');
      }
    } finally {
      setSubmitting(false);
    }
  }, [validation, updateFormData, setCurrentStep, setError, clearErrors, setSubmitting]);

  // Step 2: Profile submission
  const submitProfile = useCallback(async (formData: DieticianProfileFormData): Promise<void> => {
    setSubmitting(true);
    clearErrors();

    try {
      // Validate form data
      const validationResult = validation.validateStep(2, formData);
      if (!validationResult.isValid) {
        setError('form', validationResult.error || 'Validation failed');
        return;
      }

      // Convert photo to base64 if provided
      let photoData = undefined;
      if (formData.photo) {
        photoData = await FileUtils.safeConvertToBase64(formData.photo);
      }

      // Submit to API
      const apiData: DieticianProfileApiRequest = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: {
          country_code: formData.phone.countryCode,
          iso_code: formData.phone.isoCode,
          number: formData.phone.number
        },
        professional_info: {
          apd_id: formData.apdNumber,
          areas_of_practice: formData.areasOfPractice.map(area => String(area))
        },
        practice_address: {
          street_address: formData.practiceAddress.streetAddress,
          city: formData.practiceAddress.city,
          state: formData.practiceAddress.state,
          postal_code: formData.practiceAddress.postalCode,
          country: formData.practiceAddress.country
        },
      languages_spoken: formData.languagesSpoken,
        photo: photoData,
        availability: {
          calendar_integration: []
        },
        consent: {
          terms_of_service: true,
          privacy_policy: true,
          professional_standards: true
        },
        payment_policy: feeFieldsToPolicy(formData.feePolicy)  // always sent (design D22)
      };

      await apiService.dietician.updateProfile(apiData);

      // Update state with success
      updateFormData({
        firstName: formData.firstName,
        lastName: formData.lastName,
        areasOfPractice: formData.areasOfPractice,
        languagesSpoken: formData.languagesSpoken,
        phone: formData.phone,
        apdNumber: formData.apdNumber,
        practiceAddress: formData.practiceAddress,
        photo: formData.photo
      });

      setRegistrationState(prevState => ({
        ...prevState,
        completedSteps: new Set([...prevState.completedSteps, 2])
      }));

      // Proceed to next step
      setCurrentStep(3);

    } catch (error) {
      if (error instanceof BaseApiError && error.fieldErrors) {
        Object.entries(error.fieldErrors).forEach(([field, message]) => {
          setError(field, message);
        });
      } else {
        setError('form', error instanceof Error ? error.message : 'Profile update failed');
      }
    } finally {
      setSubmitting(false);
    }
  }, [validation, registrationState.formData, updateFormData, setCurrentStep, setError, clearErrors, setSubmitting]);

  // Step 3: Schedule submission
  const submitSchedule = useCallback(async (formData: DieticianScheduleFormData): Promise<void> => {
    setSubmitting(true);
    clearErrors();

    try {
      logger.log('formData.calendlySchedulingUri2: ', formData.calendlySchedulingUri);

      // For OAuth flow, we just need to check if connection is established
      if (formData.calendlySchedulingUri == undefined || formData.calendlySchedulingUri == null) {
        setError('form', 'Calendly connection is required');
        return;
      }

      setRegistrationState(prevState => ({
        ...prevState,
        completedSteps: new Set([...prevState.completedSteps, 3])
      }));

      // Proceed to confirmation step
      setCurrentStep(4);

    } catch (error) {
      logger.warn('error', error);
      if (error instanceof BaseApiError && error.fieldErrors) {
        Object.entries(error.fieldErrors).forEach(([field, message]) => {
          setError(field, message);
        });
      } else {
        setError('form', error instanceof Error ? error.message : 'Schedule configuration failed');
      }
    } finally {
      setSubmitting(false);
    }
  }, [setCurrentStep, setError, clearErrors, setSubmitting]);

  // Reset workflow
  const resetFlow = useCallback((): void => {
    setRegistrationState({
      currentStep: 1,
      formData: {},
      isLoading: false,
      errors: {},
      isSubmitting: false,
      completedSteps: new Set()
    });
  }, []);

  // Step determination logic on mount
  useEffect(() => {
    const determineCurrentStep = async () => {
      setLoading(true);

      try {
        // Step 1: Check if user is authenticated
        const authService = AuthServiceFactory.getInstance();
        const isAuthenticated = await authService.isAuthenticated();

        if (!isAuthenticated) {
          setCurrentStep(1);
          return;
        }

        // Step 2: Check if dietician profile exists
        try {
          const profileResponse = await apiService.dietician.getProfile();

          // Update form data with existing profile information
          if (profileResponse.data) {
            const profile = profileResponse.data;
            updateFormData({
              firstName: profile.first_name,
              lastName: profile.last_name,
              areasOfPractice: (profile.areas_of_practice || []) as HealthCondition[],
              languagesSpoken: profile.languages_spoken || [LanguageCode.English],
              phone: profile.phone ? {
                countryCode: '',
                isoCode: '',
                number: profile.phone
              } : undefined,
              apdNumber: profile.apd_id || '',
              practiceAddress: {
                streetAddress: profile.practice_street_address || '',
                city: profile.practice_city || '',
                state: profile.practice_state || '',
                postalCode: profile.practice_postal_code || '',
                country: profile.practice_country || ''
              }
            });
          }
        } catch (error) {
          // Profile doesn't exist, user needs to complete profile step
          logger.debug('Profile not found, directing to step 2:', error);
          setCurrentStep(2);
          return;
        }

        // Step 3: Check if Calendly calendar is connected
        try {
          await apiService.user.getCalendlyCalendar();
        } catch (error) {
          // Calendly not connected, user needs to complete schedule step
          logger.debug('Calendly not connected, directing to step 3:', error);
          setCurrentStep(3);
          return;
        }

        // All steps completed, show confirmation
        setCurrentStep(4);

      } catch (error) {
        logger.error('Error determining current step:', error);
        // Default to step 1 on any unexpected error
        setCurrentStep(1);
      } finally {
        setLoading(false);
      }
    };

    determineCurrentStep();
  }, [setCurrentStep, setLoading]);

  // Validation helpers
  const validateStep = useCallback((step: number): boolean => {
    const result = validation.validateStep(step, registrationState.formData);
    return result.isValid;
  }, [validation, registrationState.formData]);

  const getStepErrors = useCallback((step: number): string[] => {
    const result = validation.validateStep(step, registrationState.formData);
    if (result.isValid) {
      return [];
    }
    return [result.error || 'Validation failed'];
  }, [validation, registrationState.formData]);

  const canProceedToStep = useCallback((step: number): boolean => {
    // Can proceed if previous step is completed
    return registrationState.completedSteps.has(step - 1);
  }, [registrationState.completedSteps]);

  const isStepCompleted = useCallback((step: number): boolean => {
    return registrationState.completedSteps.has(step);
  }, [registrationState.completedSteps]);

  // Computed values
  const isFormValid = useMemo(() => {
    return validateStep(registrationState.currentStep);
  }, [validateStep, registrationState.currentStep]);

  const progressPercentage = useMemo(() => {
    return (registrationState.currentStep / 4) * 100;
  }, [registrationState.currentStep]);

  return {
    state: registrationState,
    actions: {
      setCurrentStep,
      updateFormData,
      submitRegistration,
      submitProfile,
      submitSchedule,
      resetFlow,
      setError,
      clearErrors,
      setLoading
    },
    validation: {
      validateStep,
      getStepErrors,
      canProceedToStep,
      isStepCompleted,
      isFormValid
    },
    computed: {
      progressPercentage
    }
  };
};